import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

import { NoteService } from "../db/services/note.service";
import { TaskService } from "../db/services/task.service";
import { DatabaseUtils } from "../db/utils/database";
import { analytics } from "../services/analytics";
import {
    clearPrivateProfile,
    ensurePrivateProfile,
    purgeStoredPrivateProfile,
} from "../services/privateProfileStore";
import { clearAllEditorDrafts } from "../utils/editorDraftStorage";

interface AuthContextType {
    accessToken: string | null;
    setAccessToken: (token: string | null) => void;
}

const base_url = import.meta.env.VITE_API_BASE_URL;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// localStorage keys to wipe when we force the user back to the
// signin screen. Mirrors the list in `sidebar.tsx`'s logout handler
// so a forced sign-out from a missing/expired refresh cookie leaves
// the same clean slate as a manual logout.
//
// `userId` is intentionally NOT in this list: it survives logout as
// a "last signed-in user" marker so the next sign-in can detect a
// user change and decide whether to wipe team-scoped IndexedDB.
//
// Nor are the email, phone, country, location and bio: those are never
// written to localStorage at all (MIP-619). They live in
// `privateProfileStore`, which every sign-out path clears.
export const AUTH_LOCAL_STORAGE_KEYS = [
    "isSigningIn",
    "userName",
    "avatarImgPath",
    "teamId",
    "tsJoined",
    "isOfflineForced",
    "role",
    "locationShared",
    "customStatus",
    "teamName",
    "lastOpenMyNoteId",
    "lastOpenNoteType",
    "lastChatType",
    "lastDMChatId",
    "lastGMChatId",
    "lastPMChatId",
    "lastPinnedChatId",
    "lastPinnedChatType",
    "lastProjectId",
    "lastOpenChatNoteId",
    "lastOpenTaskNoteId",
    "currentMainChatId",
];

// Device-level UI preferences that belong to "this browser for this person"
// rather than to a specific account. Preserved across user-change by
// `clearUserScopedLocalStorage` so signing in as someone else doesn't reset
// theme, locale, or layout choices.
//
// When you add a new preference hook in `hooks/common/`, add its storage
// key here too — otherwise it'll get wiped the next time a different user
// signs in on the same device.
export const DEVICE_PREFERENCE_LOCAL_STORAGE_KEYS = [
    "genos-theme-preference",
    // v2 only. This is a PRESERVE list, so the retired
    // `genos-bubble-style-preference` (v1) is deliberately absent —
    // leaving it here would carry the dead key across every user switch
    // forever; omitted, the `localStorage.clear()` below sweeps it.
    "genos-bubble-style-preference-v2",
    "genos-bubble-pinned-actions-v1",
    "genos-locale",
    "genos-spotlight-preferences:v1",
    "genos-analytics-preferences:v1",
    "genos-double-click-todo-preference:v1",
    "genos.sprintBoard.sortTiers.v2",
    "genos.taskTable.sortTiers.v2",
];

// Transient cross-auth handoff state that must survive the user-change
// wipe. A pending team invite is captured BEFORE the user authenticates,
// so it isn't "the previous user's data" — it's intent for the action the
// current sign-in/sign-up is completing. Without preserving it, the
// `localStorage.clear()` in clearUserScopedLocalStorage drops the token
// before the post-auth consume funnel (JoinTeam) can redeem it. Only the
// token: JoinTeam asks the invite preview which address it is for.
export const TRANSIENT_HANDOFF_LOCAL_STORAGE_KEYS = ["pendingInviteToken"];

/**
 * Wipe localStorage of the previously signed-in user's data while keeping
 * device-level UI preferences intact. Called from the sign-in flow when a
 * *different* user signs in on the same device, so things like editor
 * drafts, Spotlight conversation history, last-opened chat/note IDs, and
 * cached profile fields don't leak across user boundaries.
 *
 * Implemented as an allowlist sweep rather than a hardcoded removal list
 * so it transparently handles team-scoped and prefix-keyed storage
 * (`genos-editor-draft:v1:*`, `spotlight:session:v1:<teamId>`,
 * `noteTabs:<teamId>`) without needing to enumerate every variant.
 */
export const clearUserScopedLocalStorage = (): void => {
    const preserved = new Map<string, string>();
    for (const key of [
        ...DEVICE_PREFERENCE_LOCAL_STORAGE_KEYS,
        ...TRANSIENT_HANDOFF_LOCAL_STORAGE_KEYS,
    ]) {
        const val = localStorage.getItem(key);
        if (val !== null) preserved.set(key, val);
    }
    localStorage.clear();
    for (const [key, val] of preserved) {
        localStorage.setItem(key, val);
    }
    // The previous user's private profile is in memory, not storage.
    clearPrivateProfile();
};

// Outcome of a single refresh attempt. We need to distinguish three
// cases because they have different policies:
//   - "ok": got a new access token, stop the retry loop.
//   - "unauthenticated": server told us the refresh token is missing
//      / invalid / expired (HTTP 401/403). No amount of retrying will
//      fix it — we have to send the user back to /signin.
//   - "transient": network error or server hiccup. Worth retrying.
type RefreshOutcome = "ok" | "unauthenticated" | "transient";

// Exponential-backoff schedule for transient failures. We start with
// a short delay (so a quick blip recovers fast) and cap at 60 s so a
// stale tab on a long network outage doesn't hammer the API. Order
// matters; the last value is also the steady-state cap.
const REFRESH_BACKOFF_SCHEDULE_MS = [2_000, 5_000, 10_000, 20_000, 60_000];

// Abort a refresh that hasn't responded in this window. A hung request (dead
// backend, stalled connection) would otherwise never resolve, and since every
// data loader waits on `accessToken` — which this call sets — it would block
// the entire app boot indefinitely. Aborting turns it into a retryable
// `transient` failure that the backoff schedule above re-arms.
const REFRESH_TIMEOUT_MS = 10_000;

// Delays between attempts to load the private profile after a reload.
const PRIVATE_PROFILE_RETRY_MS = [2_000, 10_000];

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    // Single pending retry timer (replaces the previous flat-interval
    // approach). We re-arm it manually after each transient failure
    // using the backoff schedule above.
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const refreshAttemptCountRef = useRef(0);
    // Guard so we only fire the redirect once even if multiple
    // refresh attempts overlap (e.g. timer tick + initial call).
    const hasRedirectedRef = useRef(false);

    // Single refresh attempt. Distinguishes server-reported auth
    // failures (401/403) from transient errors so the caller can
    // decide whether to keep retrying.
    const refreshAccessToken = async (): Promise<RefreshOutcome> => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
        try {
            const response = await fetch(`${base_url}/user/signin/refresh/`, {
                method: "GET",
                credentials: "include",
                signal: controller.signal,
            });

            if (response.ok) {
                const data = await response.json();
                setAccessToken(data.access);
                return "ok";
            }

            if (response.status === 401 || response.status === 403) {
                return "unauthenticated";
            }

            console.error(`[AUTH] Token refresh failed with status ${response.status}`);
            return "transient";
        } catch (error) {
            // Network/CORS failure OR the abort timeout — treat as transient
            // and keep retrying per the backoff schedule.
            console.error("[AUTH] Token refresh failed (network or timeout)");
            console.error(error);
            return "transient";
        } finally {
            clearTimeout(timeoutId);
        }
    };

    // Force a sign-out: clear the localStorage flags AuthGuard reads,
    // null out the in-memory access token, and navigate to /signin
    // via a full-page redirect. We use `window.location.assign`
    // instead of `useNavigate` so a forced sign-out wipes the entire
    // app state (React state, cached query data, websocket
    // connections, etc.) on the way out — starting from a clean
    // slate is much safer here than a soft navigation.
    //
    // Note: AuthProvider is intentionally NOT mounted on the public
    // /home marketing page (see main.tsx), so this code path can
    // never fire there — but the path-based early return inside
    // attemptRefresh is kept as defense-in-depth for /signin and
    // /signup, which DO live inside AuthProvider.
    const forceSignOut = () => {
        if (hasRedirectedRef.current) return;
        hasRedirectedRef.current = true;

        clearRefreshTimer();
        AUTH_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.setItem(key, ""));
        localStorage.setItem("isOfflineForced", "false");
        clearPrivateProfile();
        clearAllEditorDrafts();
        analytics.reset();
        setAccessToken(null);

        // Avoid stacking redirects if the user is already on the
        // signin page (e.g. from another tab racing).
        if (window.location.pathname !== "/signin") {
            window.location.assign("/signin");
        }
    };

    // Pick the delay for the next retry from the backoff schedule.
    // Once we run off the end of the schedule we stick at the last
    // (largest) value so this stays a steady poll, not unbounded.
    const nextBackoffDelayMs = (attemptIndex: number): number => {
        const i = Math.min(attemptIndex, REFRESH_BACKOFF_SCHEDULE_MS.length - 1);
        return REFRESH_BACKOFF_SCHEDULE_MS[i];
    };

    const startTokenRefreshRetry = () => {
        if (isRefreshing) return; // Prevent multiple retry cycles

        setIsRefreshing(true);
        refreshAttemptCountRef.current = 0;

        const attemptRefresh = async () => {
            // Return if the user is on the signin or signup page.
            //
            // /accept-invite is included because it's a public-but-
            // AuthProvider page that manages its own auth routing: a
            // logged-out invitee must be sent to /signup or /signin based
            // on the invite preview, not force-redirected to /signin (which
            // would drop the invite context). For a logged-in visitor it
            // refreshes the token itself and hands it over; the actual
            // accept happens on /jointeam or via the signup endpoint.
            if (
                window.location.pathname === "/signin" ||
                window.location.pathname === "/signup" ||
                window.location.pathname === "/reset-password" ||
                window.location.pathname === "/oauth/success" ||
                window.location.pathname === "/accept-invite" ||
                window.location.pathname === "/home"
            ) {
                return;
            }

            const outcome = await refreshAccessToken();

            if (outcome === "ok") {
                clearRefreshTimer();
                return;
            }

            if (outcome === "unauthenticated") {
                forceSignOut();
                return;
            }

            // Transient: schedule the next attempt with backoff.
            const delay = nextBackoffDelayMs(refreshAttemptCountRef.current);
            refreshAttemptCountRef.current += 1;
            refreshTimeoutRef.current = setTimeout(attemptRefresh, delay);
        };

        attemptRefresh();
    };

    const clearRefreshTimer = () => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }
        refreshAttemptCountRef.current = 0;
        setIsRefreshing(false);
    };

    useEffect(() => {
        if (accessToken === null) {
            startTokenRefreshRetry();
        } else if (accessToken) {
            clearRefreshTimer(); // Cancel any pending retries
        }
    }, [accessToken]);

    useEffect(() => {
        return () => {
            clearRefreshTimer();
        };
    }, []);

    // The signed-in user's email, phone, country, location and bio are
    // kept in memory only (`privateProfileStore`, MIP-619). Delete the
    // copies earlier releases cached in localStorage, which outlived every
    // sign-out as empty-or-not keys.
    useEffect(() => {
        purgeStoredPrivateProfile();
    }, []);

    // A reload empties that store, so refill it once there is a token. A
    // sign-in has usually filled it already, which makes this a no-op. A
    // failure is retried a couple of times: the user's own profile card
    // reads these, and an empty bio there looks like one to be rewritten.
    useEffect(() => {
        if (!accessToken) return;
        let cancelled = false;
        let retryTimer: ReturnType<typeof setTimeout> | null = null;
        const attempt = async (n: number) => {
            const profile = await ensurePrivateProfile(accessToken);
            if (cancelled || profile || n >= PRIVATE_PROFILE_RETRY_MS.length) return;
            retryTimer = setTimeout(() => void attempt(n + 1), PRIVATE_PROFILE_RETRY_MS[n]);
        };
        void attempt(0);
        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [accessToken]);

    // One-shot sweep of orphaned per-document Yjs IndexedDB databases
    // (`task-body:<id>`, `my-note:<id>`, `chat-note:<id>`, `task-note:<id>`).
    // y-indexeddb creates one DB per editor document and never cleans up,
    // so deleted tasks/notes leave their CRDT logs behind forever. Per-row
    // deletes are hooked at the delete-modal call sites; this sweep cleans
    // up orphans that pre-date the hook or escaped it (e.g. soft-deleted
    // tasks that later disappear from `taskMeta`).
    //
    // Fires once per sign-in (the `hasSweptRef` guard skips later token
    // refreshes). Delayed so the post-auth fetches have time to repopulate
    // genosData — the sweep reads its allow-list from there.
    const hasSweptRef = useRef(false);
    useEffect(() => {
        if (!accessToken || hasSweptRef.current) return;
        hasSweptRef.current = true;
        const timer = setTimeout(async () => {
            try {
                const taskService = new TaskService();
                const noteService = new NoteService();
                const [tasks, myNotes, chatNotes, taskNotes] = await Promise.all([
                    taskService.getAllTasks(),
                    noteService.getAllPersonalNotes(),
                    noteService.getAllChatNotes(),
                    noteService.getAllTaskNotes(),
                ]);
                const taskIds = new Set<number>();
                for (const t of tasks) {
                    const n = Number((t as { id?: number | string }).id);
                    if (Number.isFinite(n)) taskIds.add(n);
                }
                const myNoteIds = new Set<number>(
                    myNotes
                        .map((n) => Number((n as { noteId?: number }).noteId))
                        .filter(Number.isFinite)
                );
                const chatNoteIds = new Set<number>(
                    chatNotes
                        .map((n) => Number((n as { noteId?: number }).noteId))
                        .filter(Number.isFinite)
                );
                const taskNoteIds = new Set<number>(
                    taskNotes
                        .map((n) => Number((n as { noteId?: number }).noteId))
                        .filter(Number.isFinite)
                );
                await DatabaseUtils.sweepOrphanYjsDatabases({
                    taskIds,
                    myNoteIds,
                    chatNoteIds,
                    taskNoteIds,
                });
            } catch (err) {
                console.error("[IDB] Yjs orphan sweep failed:", err);
            }
        }, 30_000);
        return () => clearTimeout(timer);
    }, [accessToken]);

    // `setAccessToken` is a stable useState setter; memo only needs to refresh
    // when `accessToken` changes. Without this, the provider's `value` is a
    // fresh object every render → every `useAuth()` consumer (and there are
    // many in the chat/notes/tasks tree) re-renders on every parent re-render.
    const value = useMemo(() => ({ accessToken, setAccessToken }), [accessToken]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};

/**
 * The access token, or `null` when there is no provider above — for
 * hooks whose behaviour merely *improves* with a token and must not
 * take the whole subtree down without one.
 *
 * The upload-size guards are the case this exists for: they degrade to
 * a permissive default when they can't resolve the tier, which is
 * strictly better than throwing. `useAuth` keeps throwing, deliberately
 * — anything that actually needs to authenticate a request should fail
 * loudly rather than silently act signed-out.
 */
export const useOptionalAccessToken = (): string | null =>
    useContext(AuthContext)?.accessToken ?? null;
